/**
 * Block 12 PR-D-B Phase G1 — B-141 universal clipboard copy.
 *
 * Workaround для двух наблюдаемых багов в production share flow:
 *  - **WhatsApp mobile:** игнорирует `ShareData.text` когда `files: [pdf]`
 *    прикреплён (Android Share Sheet передаёт только файл).
 *  - **Unigram (Telegram desktop):** игнорирует `ShareData.text` целиком.
 *
 * Решение: перед каждым `navigator.share` / fallback download — копируем
 * текст КП в clipboard. Пользователь может вставить вручную если клиент
 * не подхватил текст автоматически. Footer hint объясняет UX.
 *
 * Clipboard API может fail на insecure context (HTTP), permission denial,
 * или в browsers без Clipboard API (старые Safari). Возвращаем boolean
 * вместо throw — caller продолжает share flow без блокировки.
 */
export interface CopyShareTextOptions {
  /** DI seam для тестов — defaults to `navigator.clipboard`. */
  clipboard?: Pick<Clipboard, 'writeText'>;
}

export async function copyShareTextToClipboard(
  text: string,
  options: CopyShareTextOptions = {},
): Promise<boolean> {
  const clipboard =
    options.clipboard ??
    (typeof navigator !== 'undefined'
      ? (navigator.clipboard as Pick<Clipboard, 'writeText'> | undefined)
      : undefined);

  if (!clipboard || typeof clipboard.writeText !== 'function') {
    return false;
  }

  try {
    await clipboard.writeText(text);
    return true;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[clipboardCopy] writeText failed:', err);
    }
    return false;
  }
}
